import React, { useContext, useMemo, useState } from 'react';
import { Breadcrumbs, Crumb } from '../../../../components/Breadcrumbs';
import { Routing } from '../../../../../util/routes.util';
import { TranslationKey } from '../../../../../types/translation/TranslationKey';
import { t } from '../../../../../types/translation/Translator';
import { AddButton } from '../../../../components/Button';
import Table, { TableHeader } from '../../../../components/Table';
import { DeleteIconButton } from '../../../../icons';
import { CompanyRoleAssignmentContext } from '../../../../../context/CompanyRoleAssignmentContext';
import { CompanyContext } from '../../../../../context/CompanyContext';
import { UserContext } from '../../../../../context/UserContext';
import { User } from '../../../../../types/user';
import ProductTransactionScanRequiredAddToWhitelistModal from './ProductTransactionScanRequiredAddToWhitelistModal';
import useMutation from '../../../../../hooks/useMutation';
import {
  CompanyMutations,
  UpdateCompanyResponse,
  UpdateCompanyVariables,
} from '../../../../../graphql/company.graphql';
import { Company } from '../../../../../types/company';
import { ToastType } from '../../../../components/Toast';
import { useToast } from '../../../../../providers/ToastProvider';
import { toFilterString } from '../../../../../util/string.util';
import { Searchbar } from '../../../../components/TextInput';

export default function ProductTransactionsScanRequiredWhitelistedUsersScreen() {
  const { isAdmin } = useContext(CompanyRoleAssignmentContext);
  const { currentCompany, setCurrentCompany } = useContext(CompanyContext);
  const { companyUsers } = useContext(UserContext);
  const { showToast } = useToast();

  const scanFeatureToggle = currentCompany.settings.featureToggles.scan;
  if (!scanFeatureToggle.scanRequired) return null;

  const whitelistedUsers = currentCompany.settings.featureToggles.scan.scanRequiredWhitelist ?? [];

  const allItems = [...companyUsers.values()].filter(user => whitelistedUsers.includes(user.id));

  const [addModalOpen, setAddModalOpen] = useState<boolean>(false);

  const [query, setQuery] = useState('');

  const filteredItems = useMemo(() => {
    const q = toFilterString(query);
    return allItems.filter(item => toFilterString(item.email).includes(q));
  }, [allItems, query]);

  const unwhitelistedUsers = useMemo(
    () => Array.from(companyUsers.values()).filter(user => !whitelistedUsers.includes(user.id)),
    [companyUsers, whitelistedUsers],
  );

  const [update, { loading }] = useMutation<UpdateCompanyResponse, UpdateCompanyVariables>(CompanyMutations.update, {
    onCompleted: response => {
      setCurrentCompany(new Company(response.updateCompany));
      showToast({
        type: ToastType.success,
        title: t(TranslationKey.productTransactionScanRequiredWhitelistUpdateSuccess).singular.upper,
      });
    },
  });

  const updateCompanyWhitelist = async (newWhitelistedUsers: User[]) => {
    const newWhitelist = newWhitelistedUsers.map(user => user.id);
    const updatedCompany = new Company(currentCompany).withFeatureToggleScanRequiredWhitelist(newWhitelist);
    update({ variables: { company: updatedCompany.forUpdate() } });
  };

  const handleSelection = (userIds: Set<string>) => {
    const newWhitelist = [...new Set([...whitelistedUsers, ...userIds])];
    update({ variables: { company: currentCompany.withFeatureToggleScanRequiredWhitelist(newWhitelist).forUpdate() } });
  };

  const handleRemove = (userId: string) => {
    const newItems = allItems.filter(item => item.id !== userId);
    updateCompanyWhitelist(newItems);
  };

  const headers: TableHeader<User>[] = [
    { key: 'email', name: t(TranslationKey.email).singular.upper, text: user => user.email },
  ];

  if (isAdmin) {
    headers.push({
      key: 'delete',
      name: '',
      text: item => (
        <DeleteIconButton
          onClick={() => {
            handleRemove(item.id);
          }}
        />
      ),
    });
  }

  return (
    <>
      {addModalOpen && (
        <ProductTransactionScanRequiredAddToWhitelistModal
          open={addModalOpen}
          setOpen={setAddModalOpen}
          unwhitelistedUsers={unwhitelistedUsers}
          onSelectionConfirmed={handleSelection}
        />
      )}
      <div className='flex flex-grow flex-col'>
        <Breadcrumbs>
          <Crumb
            path={Routing.settings.featureToggles.overview()}
            text={t(TranslationKey.featureToggles).singular.upper}
          />
          <Crumb
            path={Routing.settings.featureToggles.productTransactionsScanRequiredWhitelistedUsers()}
            text={t(TranslationKey.productTransactionScanRequiredFeatureToggleTitle).singular.upper}
            current
          />
        </Breadcrumbs>
        <div className='content-x content-y-breadcrumbs flex h-full flex-col gap-4'>
          <div className='grid gap-2'>
            <p className='page-title'>
              {t(TranslationKey.productTransactionScanRequiredWhitelistManagementTitle).singular.upper}
            </p>
            <p className='subtitle'>
              {t(TranslationKey.productTransactionScanRequiredWhitelistManagementSubtitle).singular.upper}
            </p>
          </div>
          <div className='flex justify-between gap-4'>
            <Searchbar
              value={query}
              onChange={value => setQuery(value)}
              placeholder={t(TranslationKey.filterUsers).singular.label}
              clearable={true}
            />
            {isAdmin && unwhitelistedUsers.length > 0 && (
              <AddButton onClick={() => setAddModalOpen(true)} disabled={loading} />
            )}
          </div>

          <Table headers={headers} items={filteredItems} loading={loading} />
        </div>
      </div>
    </>
  );
}
